/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useContext, useEffect, useRef, useState } from "react";
import { useToasts } from "react-toast-notifications";
import { LayoutContext } from "../../components/layout";
import { Dashboard2Context } from "../dashboard2";
import fetchHelper from "../../services/fetchHelper";
import cn from "classnames";
import { Priority } from "./Priority";
import useFeatureFlag from "../../utils/featureFlag";
import { featureCheck } from "../../utils/feature";
import { STATUS_CODE } from "../../constants/GeneralConstants";
import ModalPhysicianAssignmentValidation from "./ModalPhysicianAssignmentValidation";
import { CPT_CODES_ENABLED } from "../../constants/AppConfigConstants";
const CPT_CODES_VALIDATION_ENABLED = "cpt-codes-validation-enabled";
const NEW_CPT_CODES_PRACTICES_ENABLED = "new-cpt-codes-practices-enabled";

export const AssignPhysician = ({ readPhysicians, loadAssignerInfo, reloadComponent }) => {
  const { fetchI } = useContext(Dashboard2Context);
  const { headerDetails: userInfo } = useContext(LayoutContext);
  const [value, setValue] = useState(null);
  const [oldValue, setOldValue] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const { getFlag } = useFeatureFlag();
  const timeo = useRef(null);
  const { addToast } = useToasts();

  useEffect(() => {
    if (userInfo && readPhysicians && readPhysicians.length > 0) {
      const index = readPhysicians.findIndex((o) => o.id === userInfo?.readingPhysician?.id);
      if (index !== -1) setValue(readPhysicians[index].id);
    }
  }, [readPhysicians, userInfo]);

  const updateStatus = (status) => {
    setIsSuccess(status);
    if (timeo.current) clearTimeout(timeo.current);
    timeo.current = setTimeout(() => {
      setIsSuccess(null);
    }, 3000);
  };

  const isCPTCodesPracticesEnable = () => {
    const cptCodes = getFlag(CPT_CODES_ENABLED);
    if (cptCodes.enabled && cptCodes.attributes[CPT_CODES_VALIDATION_ENABLED]) {
      const practicesForCptCodes = cptCodes.attributes[NEW_CPT_CODES_PRACTICES_ENABLED] || [];
      return practicesForCptCodes.length === 0 || practicesForCptCodes.includes(userInfo.location.id);
    }
    return false;
  };

  const updateReadPhysician = async (value, type = "ASSIGN") => {
    try {
      if (isLoading) return false;
      setIsSuccess(null);
      setIsLoading(true);
      setValue(value);
      let url = `/patientmanagement/rest/interrogations/${userInfo.id}`;
      let body = {
        readingPhysician: { id: value },
        id: userInfo.id,
      };
      if (type === "ASSIGN" && featureCheck("assignApi.enable")) {
        url = `/patientmanagement/rest/v2/interrogations/${userInfo.id}/assignEsigner/${value}`;
        body = {};
      } else if (type === "UNASSIGN") {
        body = {
          readingPhysician: { id: {} },
          id: userInfo.id,
          interrogationStatus: "NOTASSIGNED",
        };
      }
      const { status } = await fetchHelper.putV2(url, body);
      if (isCPTCodesPracticesEnable && status === STATUS_CODE[207]) {
        return setShowModal(true);
      } else {
        fetchIR(value);
      }
    } catch (err) {
      addToast("An error occurred while assigning the interrogation, please try later.", {
        appearance: "error",
        autoDismiss: false,
      });
      updateStatus(false);
      setValue(oldValue);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchIR = async (value) => {
    await fetchI(userInfo.id);
    setOldValue(value);
    updateStatus(true);
    loadAssignerInfo(userInfo.id);
    setShowModal(false);
  };

  const onChangeValue = async ({ target: { value: changeValue } }) => {
    setValue(changeValue);
    if (changeValue) {
      await updateReadPhysician(changeValue);
    } else {
      await updateReadPhysician(changeValue, "UNASSIGN");
    }
  };

  return (
    <div id="assign-physician-section" className="asgn-to-phys">
      <div className="grp-hdr">
        <h3>Assign to Physician</h3>
      </div>
      <div className="cont-flex" style={{ alignItems: "center" }}>
        <div style={{ width: 350, position: "relative" }}>
          <select onChange={onChangeValue} value={value || ""} style={{ width: "100%" }}>
            <option value="" id="assign-phy">
              Select Physician
            </option>
            {readPhysicians
              .filter((i) => i.rtDefibrillators === true || i.rtPaceMakers === true)
              .sort((a, b) => {
                if (a.family > b.family) return 1;
                if (b.family > a.family) return -1;
                return 0;
              })
              .map((user) => {
                return (
                  <option key={user.id} value={user.id}>
                    {`${user.family}, ${user.name}`}
                  </option>
                );
              })}
          </select>
        </div>
        {isLoading && !(userInfo?.readingPhysician?.id || oldValue) ? (
          <div
            className="saving-teal-ico icon-64"
            style={{ marginLeft: 5, marginRight: 15, width: 40.5, height: 20.5 }}
          />
        ) : null}
        {userInfo?.readingPhysician?.id || oldValue ? (
          <button
            className="btn btn-urgent"
            onClick={onChangeValue}
            style={{ marginLeft: 5, position: "relative", width: 200 }}
            id="unassign-btn"
            type="button"
          >
            {isLoading ? (
              <div
                id="results-loading-icon"
                className="saving-white-ico icon-64"
                style={{ marginLeft: 5, marginRight: 15, width: 20.5, height: 20.5 }}
              />
            ) : null}
            {isSuccess !== null && !isLoading ? (
              <div
                className={cn({ "check-flat-ico": isSuccess }, { "close-flat-ico": !isSuccess }, "icon-64")}
                style={{ marginLeft: 5, marginRight: 15, width: 16.5, height: 22.5 }}
              />
            ) : null}
            <span>Unassign</span>
          </button>
        ) : null}
        <Priority userInfo={userInfo} />
        {showModal ? (
          <ModalPhysicianAssignmentValidation
            reloadComponent={reloadComponent}
            fetchIR={fetchIR}
            userId={userInfo.id}
            readPhysicianId={value}
          />
        ) : null}
      </div>
    </div>
  );
};
